import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  SimpleGrid,
  HStack,
  Badge,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowCircleDown,
  ArrowCircleUp,
  Package,
  FileText,
  Tag,
  Coins,
  WarningCircle,
  MagnifyingGlass,
  Stack,
  MapPin,
  Buildings,
} from 'phosphor-react';
import { formatCurrency, formatDate, formatCPFOrCNPJ } from '../../../../../utils/FormatFunctions';
import {
  EMPRESA_DRILL_DOWN_LIMIT,
  IFiscalContabilItem,
  IEmpresaAgregada,
  useFiscalContabil,
} from '../../../../../hooks/useFiscalContabil';
import {
  calcDiferencaAbsoluta,
  getTipoColor,
  IAcumuladorGrupo,
  isItemDivergente,
  safeNumber,
} from '../constants';

interface FiscalContabilCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresa: IEmpresaAgregada | null;
}

const tipoLabel: Record<number, string> = {
  1: 'Entrada',
  2: 'Saida',
  3: 'Servico',
};

interface ItemCardProps {
  item: IFiscalContabilItem;
  showDivergencia?: boolean;
}

function ItemCard({ item, showDivergencia = false }: ItemCardProps) {
  const valorContabil = safeNumber(item.valor_contabil);
  const valor = safeNumber(item.valor);
  const diff = calcDiferencaAbsoluta(item);
  const hasDivergencia = isItemDivergente(item);
  const tipoColor = getTipoColor(item.tipo);

  return (
    <Box
      border="1px solid"
      borderColor={hasDivergencia && showDivergencia ? 'red.200' : 'gray.100'}
      borderRadius="lg"
      p={4}
      bg={hasDivergencia && showDivergencia ? 'red.50' : 'white'}
      borderLeft="3px solid"
      borderLeftColor={hasDivergencia && showDivergencia ? 'red.400' : `${tipoColor}.400`}
    >
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <FileText size={16} color="#2B6CB0" />
          <Text fontWeight="bold">Nota {item.codigo_nota}</Text>
        </HStack>
        <HStack spacing={2}>
          {hasDivergencia && showDivergencia && (
            <Badge colorScheme="red" fontSize="xs">
              <HStack spacing={1}>
                <WarningCircle size={10} weight="fill" />
                <Text>Divergente</Text>
              </HStack>
            </Badge>
          )}
          <Badge colorScheme={tipoColor} fontSize="xs">
            {tipoLabel[item.tipo] || 'Desconhecido'}
          </Badge>
        </HStack>
      </HStack>

      <SimpleGrid columns={2} spacing={2} mb={2}>
        <Box>
          <Text fontSize="xs" color="gray.500">
            Documento
          </Text>
          <Text fontSize="sm" color="gray.700">
            {item.documento || '-'}
          </Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500">
            Data Nota
          </Text>
          <Text fontSize="sm" color="gray.700">
            {item.data_nota ? formatDate(item.data_nota, 'DD/MM/YYYY') : '-'}
          </Text>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={2} spacing={2} mb={2}>
        <Box>
          <Text fontSize="xs" color="gray.500">
            Valor Contábil
          </Text>
          <Text fontSize="sm" fontWeight="bold" color={hasDivergencia ? 'red.600' : 'gray.800'}>
            {formatCurrency(valorContabil)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500">
            Valor
          </Text>
          <Text fontSize="sm" fontWeight="bold" color={hasDivergencia ? 'red.600' : 'gray.800'}>
            {formatCurrency(valor)}
          </Text>
        </Box>
      </SimpleGrid>

      {hasDivergencia && showDivergencia && (
        <Box bg="red.100" px={2} py={1} borderRadius="md" mb={2}>
          <Text fontSize="xs" color="red.700" fontWeight="bold">
            Diferenca: {formatCurrency(diff)}
          </Text>
        </Box>
      )}

      <HStack spacing={2} mt={2}>
        <Tag size={14} color="#718096" />
        <Text fontSize="xs" color="gray.500">
          Acumulador: {item.acumulador_nome || '-'} ({item.acumulador ?? '-'})
        </Text>
      </HStack>
      <Text fontSize="xs" color="gray.500">
        Credito: {item.descricao_credito || '-'} | Debito: {item.descricao_debito || '-'}
      </Text>
    </Box>
  );
}

export function FiscalContabilCompanyModal({
  isOpen,
  onClose,
  empresa,
}: FiscalContabilCompanyModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const { getFiscalContabilByEmpresa } = useFiscalContabil();

  // Reseta o filtro de busca interna ao trocar de empresa — senao o termo
  // digitado na empresa A vaza pra empresa B quando o user clica em outro card.
  useEffect(() => {
    setSearchTerm('');
  }, [empresa?.codi_emp]);

  // Drill-down sob demanda: busca as notas individuais da empresa SO ao abrir
  // o modal. Usa endpoint `/empresa/{codi_emp}/` (ja existente). Default 1000
  // notas/empresa cobre 99% dos casos sem paginacao secundaria.
  const codiEmp = empresa?.codi_emp;
  const { data: items = [], isLoading: isLoadingItems } = useQuery<IFiscalContabilItem[]>({
    queryKey: ['fiscalContabilByEmpresa', { codi_emp: codiEmp }],
    queryFn: getFiscalContabilByEmpresa,
    enabled: isOpen && !!codiEmp,
    staleTime: 2 * 60 * 1000,
  });

  // Agregados do header vem direto do `empresa` (calculados no SQL via GROUP BY).
  // Nao recalcula no front — se backend filtrou, header reflete o filtro.
  const totalValor = safeNumber(empresa?.valor_total);
  const totalEntradas = empresa?.total_entradas ?? 0;
  const totalSaidas = empresa?.total_saidas ?? 0;
  const totalServicos = empresa?.total_servicos ?? 0;
  const valorEntradas = safeNumber(empresa?.valor_entradas);
  const valorSaidas = safeNumber(empresa?.valor_saidas);
  const valorServicos = safeNumber(empresa?.valor_servicos);
  const divergenciasTotal = empresa?.divergencias ?? 0;
  const valorDivergencias = safeNumber(empresa?.valor_divergencias);

  // Items divergentes / por tipo / acumuladores: derivam de `items` retornados
  // pela query (lista de notas da empresa). Ate aqui é client-side puro.
  const {
    divergencias,
    divergenciasEntradas,
    divergenciasSaidas,
    divergenciasServicos,
    acumuladores,
  } = useMemo(() => {
    const divergentesItems = items.filter(isItemDivergente);
    const acuMap = items.reduce(
      (acc, item) => {
        const key = item.acumulador ?? -1;
        if (!acc[key]) {
          acc[key] = {
            acumulador: item.acumulador,
            acumuladorNome: item.acumulador_nome || `Acum. ${item.acumulador ?? 'N/A'}`,
            quantidade: 0,
            valorTotal: 0,
          };
        }
        acc[key].quantidade += 1;
        acc[key].valorTotal += safeNumber(item.valor_contabil);
        return acc;
      },
      {} as Record<number, IAcumuladorGrupo>,
    );

    const entradasDiv = items.filter(item => item.tipo === 1 && isItemDivergente(item)).length;
    const saidasDiv = items.filter(item => item.tipo === 2 && isItemDivergente(item)).length;
    const servicosDiv = items.filter(item => item.tipo === 3 && isItemDivergente(item)).length;

    return {
      divergencias: divergentesItems,
      divergenciasEntradas: entradasDiv,
      divergenciasSaidas: saidasDiv,
      divergenciasServicos: servicosDiv,
      acumuladores: Object.values(acuMap).sort((a, b) => b.valorTotal - a.valorTotal),
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(
      item =>
        item.codigo_nota.toString().includes(term) ||
        item.documento?.toLowerCase().includes(term) ||
        item.acumulador_nome?.toLowerCase().includes(term),
    );
  }, [items, searchTerm]);

  if (!empresa) return null;

  const hasDivergencias = divergenciasTotal > 0;
  // Truncamento: o endpoint /empresa/{codi_emp}/ pega ate 1000 notas. Empresas com
  // mais notas terao a aba "Todos" e contagens derivadas (acumuladores) limitadas.
  // Mostramos banner quando o agregado do header supera o que carregou.
  const reachedFetchLimit =
    items.length >= EMPRESA_DRILL_DOWN_LIMIT && empresa.total_registros > items.length;
  let divergenciasTabLabel: string;
  if (isLoadingItems) divergenciasTabLabel = '…';
  else if (reachedFetchLimit && divergenciasTotal > divergencias.length) {
    divergenciasTabLabel = `${divergencias.length}+`;
  } else divergenciasTabLabel = String(divergencias.length);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: '6xl' }}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxH="90vh">
        <ModalHeader pb={2}>
          <HStack spacing={3} align="flex-start">
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg="blue.50"
              p={2}
              flexShrink={0}
            >
              <Buildings color="#2B6CB0" size={24} />
            </Flex>
            <Box flex={1}>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {empresa.razao_social || `Empresa ${empresa.codi_emp}`}
              </Text>
              {(empresa.razao_social || empresa.cnpj) && (
                <HStack spacing={4} mt={1} flexWrap="wrap">
                  {empresa.cnpj && (
                    <Text fontSize="sm" color="gray.500">
                      CNPJ: {formatCPFOrCNPJ(empresa.cnpj)}
                    </Text>
                  )}
                  {(empresa.cidade || empresa.uf) && (
                    <HStack spacing={1}>
                      <MapPin size={14} color="#718096" />
                      <Text fontSize="sm" color="gray.500">
                        {[empresa.cidade, empresa.uf].filter(Boolean).join(' - ')}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              )}
              {!empresa.razao_social && !empresa.cnpj && (
                <Text fontSize="sm" color="gray.400" mt={1}>
                  Cod: {empresa.codi_emp}
                </Text>
              )}
              <HStack spacing={2} mt={2}>
                <Badge colorScheme="blue" fontSize="xs">
                  {empresa.total_registros} registros
                </Badge>
                {hasDivergencias && (
                  <Badge colorScheme="red" fontSize="xs">
                    <HStack spacing={1}>
                      <WarningCircle size={10} weight="fill" />
                      <Text>{divergenciasTotal} divergencias</Text>
                    </HStack>
                  </Badge>
                )}
              </HStack>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
            <Box border="1px solid" borderColor="gray.100" borderRadius="lg" p={3}>
              <HStack spacing={2} mb={1}>
                <Coins size={16} color="#2B6CB0" />
                <Text fontSize="sm" fontWeight="medium">
                  Valor total
                </Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold">
                {formatCurrency(totalValor)}
              </Text>
              <Text fontSize="xs" color="gray.400">
                {empresa.total_registros} registros
              </Text>
            </Box>
            <Box
              border="1px solid"
              borderColor="gray.100"
              borderRadius="lg"
              p={3}
              borderLeft="3px solid"
              borderLeftColor="green.400"
              position="relative"
            >
              {divergenciasEntradas > 0 && (
                <Badge colorScheme="red" fontSize="9px" position="absolute" top={1} right={1}>
                  {divergenciasEntradas} div
                </Badge>
              )}
              <HStack spacing={2} mb={1}>
                <ArrowCircleDown size={16} color="#38A169" />
                <Text fontSize="sm" fontWeight="medium">
                  Entradas
                </Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold">
                {totalEntradas}
              </Text>
              <Text fontSize="xs" color="gray.400">
                {formatCurrency(valorEntradas)}
              </Text>
            </Box>
            <Box
              border="1px solid"
              borderColor="gray.100"
              borderRadius="lg"
              p={3}
              borderLeft="3px solid"
              borderLeftColor="orange.400"
              position="relative"
            >
              {divergenciasSaidas > 0 && (
                <Badge colorScheme="red" fontSize="9px" position="absolute" top={1} right={1}>
                  {divergenciasSaidas} div
                </Badge>
              )}
              <HStack spacing={2} mb={1}>
                <ArrowCircleUp size={16} color="#DD6B20" />
                <Text fontSize="sm" fontWeight="medium">
                  Saídas
                </Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold">
                {totalSaidas}
              </Text>
              <Text fontSize="xs" color="gray.400">
                {formatCurrency(valorSaidas)}
              </Text>
            </Box>
            <Box
              border="1px solid"
              borderColor="gray.100"
              borderRadius="lg"
              p={3}
              borderLeft="3px solid"
              borderLeftColor="purple.400"
              position="relative"
            >
              {divergenciasServicos > 0 && (
                <Badge colorScheme="red" fontSize="9px" position="absolute" top={1} right={1}>
                  {divergenciasServicos} div
                </Badge>
              )}
              <HStack spacing={2} mb={1}>
                <Package size={16} color="#805AD5" />
                <Text fontSize="sm" fontWeight="medium">
                  Serviços
                </Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold">
                {totalServicos}
              </Text>
              <Text fontSize="xs" color="gray.400">
                {formatCurrency(valorServicos)}
              </Text>
            </Box>
            <Box
              border="1px solid"
              borderColor={hasDivergencias ? 'red.200' : 'gray.100'}
              borderRadius="lg"
              p={3}
              borderLeft="3px solid"
              borderLeftColor={hasDivergencias ? 'red.400' : 'gray.300'}
              bg={hasDivergencias ? 'red.50' : 'white'}
            >
              <HStack spacing={2} mb={1}>
                <WarningCircle size={16} color={hasDivergencias ? '#E53E3E' : '#A0AEC0'} />
                <Text fontSize="sm" fontWeight="medium">
                  Divergências
                </Text>
              </HStack>
              <Text
                fontSize="lg"
                fontWeight="bold"
                color={hasDivergencias ? 'red.600' : 'gray.600'}
              >
                {divergenciasTotal}
              </Text>
              <Text fontSize="xs" color={hasDivergencias ? 'red.500' : 'gray.400'}>
                {hasDivergencias ? formatCurrency(valorDivergencias) : 'Sem divergências'}
              </Text>
            </Box>
          </SimpleGrid>

          <Divider mb={4} />

          {isLoadingItems && (
            <Flex justify="center" align="center" py={10}>
              <Spinner color="blue.500" size="lg" thickness="3px" />
              <Text ml={3} color="gray.500">
                Carregando notas da empresa…
              </Text>
            </Flex>
          )}

          {!isLoadingItems && reachedFetchLimit && (
            <Box
              bg="yellow.50"
              border="1px solid"
              borderColor="yellow.200"
              borderRadius="lg"
              p={3}
              mb={4}
              fontSize="sm"
              color="yellow.800"
            >
              Esta empresa tem {empresa.total_registros} notas no total, mas o detalhe carrega no
              maximo {EMPRESA_DRILL_DOWN_LIMIT}. As listagens abaixo (e contagens por acumulador)
              refletem so o subconjunto carregado; os totais do header vem agregados do banco.
            </Box>
          )}

          {!isLoadingItems && (
            <Tabs variant="enclosed" colorScheme="blue" defaultIndex={1}>
              <TabList>
                <Tab>
                  Todos ({items.length}
                  {reachedFetchLimit ? '+' : ''})
                </Tab>
                <Tab>
                  <HStack spacing={1}>
                    {hasDivergencias && (
                      <Icon as={WarningCircle} boxSize={4} color="red.500" weight="fill" />
                    )}
                    {/* Mostra divergencias.length (notas baixadas). Sufixo '+' indica que
                       a query atingiu limit=1000 e o agregado global tem mais. */}
                    <Text>Divergências ({divergenciasTabLabel})</Text>
                  </HStack>
                </Tab>
                <Tab>
                  <HStack spacing={1}>
                    <Icon as={Stack} boxSize={4} color="blue.500" />
                    <Text>
                      Acumuladores ({acumuladores.length}
                      {reachedFetchLimit ? '+' : ''})
                    </Text>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <InputGroup mb={4}>
                    <InputLeftElement>
                      <MagnifyingGlass size={18} color="#A0AEC0" />
                    </InputLeftElement>
                    <Input
                      placeholder="Buscar por nota, documento ou acumulador..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {filteredItems.map(item => (
                      <ItemCard key={item.id} item={item} showDivergencia />
                    ))}
                  </SimpleGrid>

                  {filteredItems.length === 0 && (
                    <Flex justify="center" py={8}>
                      <Text color="gray.500">Nenhum registro encontrado</Text>
                    </Flex>
                  )}
                </TabPanel>

                <TabPanel px={0}>
                  {hasDivergencias ? (
                    <>
                      <Box bg="red.50" p={3} borderRadius="lg" mb={4}>
                        <Text fontSize="sm" color="red.700">
                          Registros onde <strong>valor_contabil</strong> difere do{' '}
                          <strong>valor</strong>. A diferença total é de{' '}
                          <strong>{formatCurrency(valorDivergencias)}</strong>
                          {/* Quando o drill-down atinge o limite (1000 notas), o sum dos
                              items locais nao bate com o agregado do banco. Usamos sempre
                              o agregado e adicionamos ressalva quando relevante. */}
                          {reachedFetchLimit && (
                            <Text as="span" fontSize="xs" color="yellow.700" ml={1}>
                              (valor agregado do banco; listagem abaixo limitada às primeiras 1000
                              notas)
                            </Text>
                          )}
                        </Text>
                      </Box>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {divergencias.map(item => (
                          <ItemCard key={item.id} item={item} showDivergencia />
                        ))}
                      </SimpleGrid>
                    </>
                  ) : (
                    <Flex justify="center" align="center" py={8} bg="green.50" borderRadius="lg">
                      <Text color="green.600" fontWeight="medium">
                        Nenhuma divergência encontrada nesta empresa
                      </Text>
                    </Flex>
                  )}
                </TabPanel>

                <TabPanel px={0}>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {acumuladores.map(acu => (
                      <Box
                        key={acu.acumulador ?? 'null'}
                        p={4}
                        bg="gray.50"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={2}>
                            <Tag size={16} color="#2B6CB0" />
                            <Text fontWeight="bold" color="gray.700" noOfLines={1}>
                              {acu.acumuladorNome}
                            </Text>
                          </HStack>
                          <Badge colorScheme="blue">{acu.quantidade} reg.</Badge>
                        </HStack>
                        <Text fontSize="lg" fontWeight="bold" color="blue.600">
                          {formatCurrency(acu.valorTotal)}
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>

                  {acumuladores.length === 0 && (
                    <Flex justify="center" py={8}>
                      <Text color="gray.500">Nenhum acumulador encontrado</Text>
                    </Flex>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
